<template>
  <LoadingSpinner v-if="isLoading" />

  <div v-else-if="isError" class="no-print">
    <div class="text-center mt-5">
      <h1 class="text-2xl">Error loading purchase order</h1>
      <p class="text-lg">Please try again later</p>
    </div>
  </div>

  <div v-else>
    <!-- menu -->
    <div class="flex justify-between items-center bg-white p-4 shadow-md fixed top-0 left-0 right-0 z-10">
      <!-- logo -->
      <div>
        <img :src="logo" alt="uttrly logo" class="h-10" />
      </div>
    </div>

    <div class="w-full">
      <div class="mt-20 max-w-screen-md mx-4 md:mx-8 lg:mx-auto">
        <div class="pt-10 mb-4 md:mb-12 text-white">
          <h2>Purchase Order for {{ vendor.name }}</h2>
          <span v-if="vendor?.address" class="mt-2 text-base inline-block">{{ vendor.address }}</span>
        </div>

        <!-- purchase order details -->
        <div class="paper md:mt-4 px-6 pt-8 pb-12 rounded-lg text-sm">
          <!-- header informations -->
          <div class="grid grid-cols-2 mb-8 border-b border-slate-300 pb-4">
            <!-- uttrly informations -->
            <div>
              <div class="mb-4">
                <span class="block font-bold text-md mb-3">Uttrly</span>
                <span class="block mb-2">11810 Grand Park Ave, Suite 500</span>
                <span class="block mb-2">North Bethesda, MD 20852</span>
                <span class="block mb-2">202.681.0825</span>
                <span class="block">portal-admin@uttrly.com</span>
              </div>

              <div class="text-primary-700 font-semibold mb-4 text-2xl">
                <span>Purchase Order</span>
              </div>

              <!-- vendor informations -->
              <div>
                <span v-if="vendor?.name" class="block mb-1 font-bold">{{ vendor.name }}</span>
                <span v-if="vendor?.address" class="block mb-1">{{ vendor.address }}</span>
              </div>
            </div>

            <!-- log and purchase order header informations -->
            <div>
              <div class="flex justify-end mb-7">
                <img
                  :src="logo"
                  alt="Uttrly"
                />
              </div>
              <div class="text-right">
                <div class="grid grid-cols-4 gap-2">
                  <span class="font-bold col-span-3">PO#</span>
                  <span>{{ purchaseOrder.purchaseOrderId }}</span>
                </div>

                <div class="grid grid-cols-4 gap-2">
                  <span class="font-bold col-span-3">DATE</span>
                  <span>{{ getLocalDate(purchaseOrder.date) }}</span>
                </div>

                <div class="grid grid-cols-4 gap-2">
                  <span class="font-bold col-span-3">TERMS</span>
                  <span>Net 30</span>
                </div>
              </div>
            </div>
          </div>

          <!-- purchase order items -->
          <div>
            <BaseTable
              :items="purchaseOrder.items"
              :fields="purchaseOrderItemFields"
              :show-footer="true"
              class="mt-5"
            >
              <template #cost-data="{ row }">
                {{ currency(row.cost) }}
              </template>
              <template #total-data="{ row }">
                {{ currency(row.quantity * row.cost) }}
              </template>
              <template #description-data="{ row }">
                <span class="!text-wrap">{{ row.description }}</span>
              </template>

              <template #footer>
                <div class="flex items-center justify-between gap-2">
                  <div class="flex-grow">
                    <span class="text-slate-500 text-xs">Thank you for your business and have a great day</span>
                  </div>

                  <div>
                    <div class="flex items-center justify-end gap-2">
                      <span class="font-bold">Total:</span>
                      <span>{{ currency(purchaseOrder.total) }}</span>
                    </div>
                  </div>
                </div>
              </template>
            </BaseTable>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import FirebaseService from '@/services/FirebaseService'
import { useNotification } from '@kyvg/vue3-notification'
import { useQuery } from '@tanstack/vue-query'
import { useHead } from '@unhead/vue'
import { httpsCallable } from 'firebase/functions'
import { extend } from 'underscore'
import { ref } from 'vue'
import { useRoute } from 'vue-router'
import logo from '@/assets/images/logo.png'
import currency from '@/utils/currency'
import { getLocalDate } from '@/utils'
import BaseTable from '@/components/base/BaseTable.vue'
import { Vendor } from '@/models/vendor'
import { PurchaseOrder } from '@/models/purchaseOrder'


useHead({ title: 'Purchase Order' })

const { notify } = useNotification()
const $route = useRoute()
const id = $route.params.id as string

const vendor = ref(new Vendor())
const purchaseOrder = ref(new PurchaseOrder())
const purchaseOrderItemFields = [
  { key: 'name', label: 'Items' },
  { key: 'description', label: 'Description' },
  { key: 'cost', label: 'Price' },
  { key: 'quantity', label: 'Quantity' },
  { key: 'total', label: 'Total' }
]

const { isLoading, isError } = useQuery({
  queryKey: ['purchaseOrder', id],
  queryFn: async () => {
    const functions = await FirebaseService.functions()
    const fetchData = httpsCallable(functions, 'purchaseOrder-fetchPurchaseOrderV2')
    const response = await fetchData({ id })

    try {
      const data = response.data as any
      vendor.value = extend(vendor.value, data.vendor) as Vendor
      purchaseOrder.value = extend(purchaseOrder.value, data.purchaseOrder) as PurchaseOrder


      return { vendor: vendor.value, purchaseOrder: purchaseOrder.value }
    } catch (err) {
      console.log(err)
      notify({
        type: 'error',
        title: 'Error',
        text: 'Error trying to fetch data'
      })
    }
  }
})

</script>
